import classNames from "classnames";
import * as React from "react";

import type { RoboqlQuery } from "@/shared/domain/query";
import { Code as CodeIcon, X as XIcon } from "@/shared/foundation/icons";
import { Button } from "@/shared/foundation/primitives/Button";
import { Popover } from "@/shared/foundation/primitives/Popover";
import { pluralize } from "@/shared/text";
import type { SearchQueryBody } from "@/types";

import type { RoboqlMenuConfig } from "../FilterModel";
import { RoboqlFilterMenu } from "../RoboqlFilterMenu";

import styles from "./ActiveFilters.module.css";
import roboqlStyles from "./RoboqlActiveFilters.module.css";
import { RoboqlSnippet } from "./RoboqlSnippet";

/** Which surface the chip's single popup is currently showing. */
type ChipPopupMode = "preview" | "edit";

export interface RoboqlActiveFiltersProps {
  /** The active roboql query — rendered in the chip and pre-fills
   *  the editor when the user clicks the chip body to edit. */
  query: RoboqlQuery;
  /** Fires when the user clicks `×` on the chip. The hook collapses the
   *  state to `"none"` regardless of the index argument (roboql is
   *  single-valued); we pass `0` for symmetry with the structured
   *  variant's `onRemove(index)` signature. */
  onRemove: (index: number) => void;
  /** Fires from the Clear button on the right. Drops all state. */
  onClear: () => void;
  /** Rendered in the trailing group, ahead of Clear. */
  trailingActions?: React.ReactNode;
  /** Fires when the editor commits via Execute. The parent hook routes
   *  this through `view.commit({ type: "roboql", query, spec })`. */
  onExecute: (query: string, spec: SearchQueryBody) => void;
  /** Config for the chip-edit popover (target + autocomplete context). */
  roboql: RoboqlMenuConfig;
}

/**
 * Chip-row variant rendered when `filters.type === "roboql"`. Single
 * chip showing the query itself, plus a click-to-edit popover that
 * mounts `<RoboqlFilterMenu>` directly (no mode bar). Different from
 * the outer `<ResourceView.Filters>` trigger, which uses the
 * mode-switching `<FilterMenu>` shell — when the user already has an
 * active RoboQL filter, the chip-edit gesture is "open the editor on
 * this query," not "switch query mode."
 *
 * Hover preview and editor are one popover with two bodies rather than two
 * popovers: they share an anchor, and only one of them may ever be on screen,
 * so a single open state is the whole state machine. Which body renders is
 * decided by *why* the popover opened.
 */
export function RoboqlActiveFilters({
  query,
  onRemove,
  onClear,
  trailingActions,
  onExecute,
  roboql,
}: RoboqlActiveFiltersProps) {
  const [mode, setMode] = React.useState<ChipPopupMode | null>(null);
  const editing = mode === "edit";

  const showPreview = React.useCallback(() => {
    setMode((current) => current ?? "preview");
  }, []);

  return (
    <div
      className={classNames(styles.bar, roboqlStyles.roboqlBar)}
      role="region"
      aria-label="Active filters"
    >
      {/* Non-modal by default, so the chip's `×` stays clickable and visible to
          assistive tech while the editor is open. */}
      <Popover
        open={mode !== null}
        onOpenChange={(open, { reason }) => {
          // Presses are owned by the trigger's own `onClick` below, so Base
          // UI's press handling is ignored in both directions.
          if (reason === "trigger-press") {
            return;
          }
          if (!open) {
            // The editor is not hover-owned: pointing away from the chip must
            // not discard an edit in progress. Every other reason closes.
            setMode((current) =>
              reason === "trigger-hover" && current === "edit" ? current : null,
            );
            return;
          }
          // Hover offers the preview, but never demotes an open editor.
          showPreview();
        }}
      >
        <RoboqlChip
          query={query}
          onRemove={() => onRemove(0)}
          editing={editing}
          onTriggerClick={() => {
            // Show edit roboql popover on click
            setMode((current) => (current === "edit" ? null : "edit"));
          }}
        />
        <Popover.Content
          // `initialFocus` is read when the popup mounts, so switching modes
          // in place would leave focus outside a newly-opened editor. Keying
          // on the mode remounts it and re-runs the focus decision.
          key={mode}
          className={
            editing ? roboqlStyles.editPopup : roboqlStyles.previewPopup
          }
          // Nothing in a hover preview is actionable, so a hover-open must
          // leave the user's focus where it was.
          initialFocus={editing}
          aria-label={editing ? "Edit RoboQL filter" : undefined}
          data-testid={
            editing ? "roboql-edit-popover" : "roboql-preview-popover"
          }
        >
          {editing ? (
            <RoboqlFilterMenu
              initialValue={query.toString()}
              target={roboql.target}
              onExecute={(next, spec) => {
                onExecute(next, spec);
                setMode(null);
              }}
              onCancel={() => setMode(null)}
            />
          ) : (
            <RoboqlSnippet query={query} variant="full" />
          )}
        </Popover.Content>
      </Popover>
      <div className={classNames(styles.trailing, roboqlStyles.roboqlTrailing)}>
        {trailingActions}
        <Button variant="text" size="small" color="inherit" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

interface RoboqlChipProps {
  /** Full query — shown first-line-only, but exposed whole to
   *  assistive tech so truncation costs a screen-reader user nothing. */
  query: RoboqlQuery;
  onRemove: () => void;
  /** Whether the editor (rather than the preview) is open. */
  editing: boolean;
  onTriggerClick: React.MouseEventHandler<HTMLElement>;
}

/** Must be rendered inside the `<Popover>`: the chip body *is* the trigger. */
function RoboqlChip({
  query,
  onRemove,
  editing,
  onTriggerClick,
}: RoboqlChipProps) {
  const extraLines = query.lineCount - 1;

  return (
    <span
      className={roboqlStyles.chip}
      data-editing={editing ? "true" : undefined}
    >
      <Popover.Trigger
        openOnHover
        delay={0}
        className={roboqlStyles.chipBody}
        aria-label={query.toString()}
        onClick={onTriggerClick}
      >
        <CodeIcon className={roboqlStyles.chipIcon} aria-hidden="true" />
        <RoboqlSnippet query={query} variant="chip" />
        {/* The trigger's accessible name is already the whole query, so
            announcing the count of lines it hides would only repeat it. */}
        {query.isMultiline && (
          <span
            className={roboqlStyles.chipMoreLines}
            data-testid="roboql-more-lines"
            aria-hidden="true"
          >
            +{extraLines} {pluralize("line", extraLines)}
          </span>
        )}
      </Popover.Trigger>
      <button
        type="button"
        className={roboqlStyles.chipRemove}
        onClick={onRemove}
        aria-label="Remove RoboQL filter"
      >
        <XIcon className={roboqlStyles.chipRemoveIcon} aria-hidden="true" />
      </button>
    </span>
  );
}
